import { useEffect, useMemo, useRef } from 'react';
import tw, { styled } from 'twin.macro';
import { useAtom, useSetAtom } from 'jotai';
import debounce from 'lodash.debounce';
import { useTranslation } from 'next-i18next';
import { useIntercom } from 'react-use-intercom';

import { SkeletonTextLoading, SkeletonEmployerCardLoading } from '@restworld/ui-ds';

import useEmployerRestaurantsQuery from './hooks/use-employer-restaurants';
import EmployerRestaurantsFilters, { computeRestaurantFiltersCount } from './employer-restaurants-filters';
import EmployerPrimaryLayout from '../../layouts/employer-primary-layout';
import {
  restaurantSearchQueryAtom,
  employerRestaurantJpStatusFilterAtom,
  employerRestaurantLocationFilterAtom,
  showContactRestWorldManagerModalAtom,
} from './employer-restaurants-filters/employer-restaurants.atoms';
import { IEmployerFilterForm } from '../job-listing/employer-job-listing-filters';
import RestaurantOnboardingModal, { restaurantOnboardingModalVisibleAtom } from './restaurant-onboarding-modal';
import useEmployerRestaurantCards from './hooks/use-employer-restaurant-cards';

const CardsContainer = styled.div`
  ${tw`grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3 pt-2`}
`;
export const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL;
const showRestaurantOnBoarding = process.env.NEXT_PUBLIC_SHOW_RESTAURANT_ONBOARDING;
function RestaurantsPage() {
  const { t: tC } = useTranslation('common');
  const { startTour } = useIntercom();
  const [, setSeachQuery] = useAtom(restaurantSearchQueryAtom);
  const [jpStatus, setJpStatus] = useAtom(employerRestaurantJpStatusFilterAtom);
  const [location, setLocation] = useAtom(employerRestaurantLocationFilterAtom);
  const setRestaurantOnboardingModalVisible = useSetAtom(restaurantOnboardingModalVisibleAtom);
  const setShowContactRestworldManagerModal = useSetAtom(showContactRestWorldManagerModalAtom);
  const filterFormRef = useRef<IEmployerFilterForm | null>(null);
  const { restaurants, hits, query, hasNextPage } = useEmployerRestaurantsQuery();
  const restaurantCards = useEmployerRestaurantCards({ restaurants });
  const restaurantFiltersCount = useMemo(() => computeRestaurantFiltersCount(jpStatus, location), [jpStatus, location]);

  const totalSearchResults = useMemo(
    () =>
      query.isLoading ? (
        <SkeletonTextLoading width="medium" tw="mt-2" />
      ) : (
        `${hits || 0} ${hits === 1 ? tC('venue') : tC('venues')}`
      ),
    [hits, query.isLoading, tC]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const jpStatus = urlParams.get('jpStatus');
    const location = urlParams.get('location');
    if (jpStatus) setJpStatus([jpStatus]);
    if (location) setLocation(location);
  }, [setJpStatus, setLocation]);

  const employerPrimaryLayoutprops = useMemo(
    () => ({
      totalSearchResults,
      searchProps: {
        placeholder: 'what_restaurant_are_you_looking_for',
        onChange: debounce((value) => setSeachQuery(value), 500),
      },
      addButtonTitle: 'add_restaurant',
      pageTitle: 'venues_key',
      filterProps: {
        element: <EmployerRestaurantsFilters ref={filterFormRef} />,
        filterCount: restaurantFiltersCount,
        formRef: filterFormRef,
      },
      noResultsFoundProps: {
        title: 'no_restaurants_found_title',
        content: 'no_restaurants_found_content',
        foundNoResults: !query.isLoading && restaurants.length === 0,
      },
      title: 'venues_key',
      onAddButtonClick: () => {
        if (showRestaurantOnBoarding === 'true') setRestaurantOnboardingModalVisible(true);
        else setShowContactRestworldManagerModal(true);
      },
      loadMoreBtnProps: {
        onClick: () => query.fetchNextPage(),
        loading: query.isFetchingNextPage,
        hasNextPage,
      },
      loading: query.isLoading,
      onInfoIconClick: () => startTour(599325),
      isResultAndFilterElAlwaysOnSameLine: true,
    }),
    [
      hasNextPage,
      query,
      restaurantFiltersCount,
      restaurants?.length,
      setRestaurantOnboardingModalVisible,
      setSeachQuery,
      setShowContactRestworldManagerModal,
      startTour,
      totalSearchResults,
    ]
  );

  return (
    <>
      <EmployerPrimaryLayout {...employerPrimaryLayoutprops}>
        {query.isLoading ? (
          <div tw="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SkeletonEmployerCardLoading />
            <SkeletonEmployerCardLoading />
          </div>
        ) : (
          <CardsContainer>{restaurantCards}</CardsContainer>
        )}
      </EmployerPrimaryLayout>
      <RestaurantOnboardingModal />
    </>
  );
}

export default RestaurantsPage;
